import * as React from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { toast } from 'sonner'
import type { DragStartEvent, DragEndEvent } from '@dnd-kit/core'
import {
  GripVertical,
  Loader2,
  Search,
  ChevronDown,
  Filter,
  LayoutGrid,
  Table as TableIcon,
} from 'lucide-react'
import type { Meeting } from '@/services/meeting'
import { getMeetingList, meetingAcceptReject, deleteMeetingById } from '@/services/meeting'
import { setMeetingMute } from '@/services/meetings/mute'
import {
  findMeetingColumn,
  resolveAcceptRejectForMeetingMove,
  canDragMeetingOnPendingBoard,
  canAcceptRejectMeeting,
  applyOptimisticMeetingStatus,
  type PendingMeetingColumnId,
} from '@/lib/meeting-kanban-dnd'
import {
  notifyMeetingListSyncFromMutation,
  subscribeMeetingListSync,
} from '@/lib/meeting-list-cross-tab-sync'
import { useSocket } from '@/hooks/use-socket'
import { useMeetingMuteHydrate } from '@/hooks/use-meeting-mute-hydrate'
import {
  pendingOptimisticMeetingIds,
  filterOutPendingOptimisticMeetings,
  reinjectPendingOptimisticMeetings,
} from '@/lib/meeting-optimistic'
import { queryKeys } from '@/lib/query/keys'
import { partitionDeletableMeetings, resolvePinToggles } from '@/lib/meeting-bulk'
import { useAuthStore } from '@/stores/auth-store'
import { cn, getInitials } from '@/lib/utils'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useMeetingStore, meetingStore } from '@/stores/meeting-store'
import { useMeetingPinStore } from '@/stores/meeting-pin-store'
import { useMeetingMuteStore } from '@/stores/meeting-mute-store'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { MeetingCardKebab } from '@/components/meetings/meeting-card-kebab'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { mapMeetingToEvent } from '@/components/today/mappers'
import { MeetingCard } from '@/components/meetings/meeting-card'
import { MeetingCardContextMenu } from '@/components/meetings/meeting-card-context-menu'
import { getMeetingAssignees } from '@/components/meetings/meeting-assignees'
import { useMeetingUnreadCounts } from '@/hooks/use-meeting-unread-counts'
import { CreateMeetingDialog } from '@/components/meetings/create-meeting-dialog'
import { CompletedInsightsCard } from '@/components/meetings/completed-insights-card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Kanban,
  KanbanBoard,
  KanbanColumn,
  KanbanColumnHandle,
  KanbanItem,
  KanbanOverlay,
} from '@/components/ui/kanban'
import { ChartContainer, ChartTooltip, type ChartConfig } from '@/components/ui/chart'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { PieChart, Pie, Cell } from 'recharts'

export const Route = createFileRoute('/(app)/productivity/meetings/')({
  component: MeetingsPage,
})

/** Pending board columns: No Action, Attending, Not Attending, Attended (performed, awaiting close) */
const PENDING_COLUMNS = ['noAction', 'attending', 'notAttending', 'attended'] as const
type PendingColumnId = (typeof PENDING_COLUMNS)[number]

const PENDING_TITLES: Record<PendingColumnId, string> = {
  noAction: 'No Action',
  attending: 'Attending',
  notAttending: 'Not Attending',
  attended: 'Attended',
}

/** Backend `MeetingTransaction` "Upcoming": meeting whose start_date is still in the future. */
function isUpcomingMeeting(startDate: string | null | undefined): boolean {
  if (!startDate) return false
  const ts = new Date(startDate).getTime()
  return !Number.isNaN(ts) && ts >= Date.now()
}

/**
 * Local calendar-day bucket of a meeting's start date — used by the Today /
 * Upcoming date filters. `start_date` is stored as UTC (same parse the details
 * sheet uses for display), and we compare the meeting's *local* calendar day to
 * today so "Today" matches the date the user sees on the card.
 *   - `today`    → same calendar day as now
 *   - `upcoming` → a future calendar day (tomorrow onward)
 *   - `past`     → an earlier calendar day
 */
function meetingDayBucket(
  startDate: string | null | undefined,
): 'today' | 'upcoming' | 'past' | null {
  if (!startDate) return null
  const d = new Date(startDate + ' UTC')
  if (Number.isNaN(d.getTime())) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const diffDays = Math.round((day.getTime() - today.getTime()) / 86_400_000)
  if (diffDays === 0) return 'today'
  if (diffDays > 0) return 'upcoming'
  return 'past'
}

/** Completed board columns: Attended, Missed (RSVP'd Attending but didn't show), Not Attended (Skipped / No Action) */
const COMPLETED_COLUMNS = ['attended', 'missed', 'notAttending'] as const
type CompletedColumnId = (typeof COMPLETED_COLUMNS)[number]

const COMPLETED_TITLES: Record<CompletedColumnId, string> = {
  attended: 'Attended',
  missed: 'Missed',
  notAttending: 'Not Attended',
}

type StatusFilter = 'pending' | 'completed'

/**
 * Determine pending column based on the same source as the card badge.
 *
 * The card badge (see `mappers.ts:118-144` + `getMeetingStatusLabel` in
 * `meeting-event-card.tsx:91-100`) reads from the first member with
 * `is_assigned === '1'` and requires `action_date` to be set before showing
 * "Attending" / "Attended" / "Skipped". If we sourced the column from a
 * different field (e.g. the current user's member row, which is `null` for
 * a creator viewing their own meeting), the badge says "Attending" but the
 * card lands in "No Action" — the exact bug we're fixing here.
 *
 * Prefer the current user's row when they ARE one of the assignees (so a
 * receiver still sees their own status), else fall back to the first
 * assigned member (so a creator sees the receiver's status, matching the
 * badge).
 */
function getPendingColumnId(meeting: Meeting, userId: number | undefined): PendingColumnId {
  const assigned = (meeting.members ?? []).filter((m) => String(m.is_assigned) === '1')
  const me = userId != null ? assigned.find((m) => m.user_id === userId) : null
  const member = me ?? assigned[0]
  const s = (member?.status ?? meeting.status ?? '').trim().toLowerCase()
  const actionDate = member?.action_date
  const hasActionDate = actionDate != null && actionDate !== ''

  // Performed = the assignee attended. The meeting is still "Pending" because the creator
  // hasn't closed it (is_completed=0) / the end-time scheduler hasn't run — so it waits in the
  // pending "Attended" lane (mirrors how tasks park a Performed task in "Done"/Awaiting Review)
  // until is_completed flips, then it moves to Completed → Attended.
  if (s === 'performed') return 'attended'
  // Skip → Not Attending (backend excludes Skip from Upcoming).
  if ((s === 'skip' || s === 'skipped') && hasActionDate) return 'notAttending'
  if ((s === 'accept' || s === 'accepted') && hasActionDate) return 'attending'
  // TODO(upcoming-logic): the Upcoming lane was removed from the board. Future, un-responded
  // meetings currently park in No Action; the dedicated handling is the next task.
  if (s !== 'performed' && isUpcomingMeeting(meeting.start_date)) return 'noAction'
  return 'noAction'
}

/**
 * Determine completed column from the relevant member's status (same source as the
 * card badge — current user's row if assignee, else first assigned member):
 * - Performed → Attended
 * - Accept (+ action_date, i.e. the "Attending" badge) → Missed (RSVP'd yes, never showed)
 * - Skip / Pending / anything else → Not Attended
 * Backend has no native "Missed" status (statuses are Pending/Accept/Skip/Performed);
 * Missed is derived from Accept-without-Performed on a completed meeting.
 */
function getCompletedColumnId(meeting: Meeting, userId: number | undefined): CompletedColumnId {
  const assigned = (meeting.members ?? []).filter((m) => String(m.is_assigned) === '1')
  const me = userId != null ? assigned.find((m) => m.user_id === userId) : null
  const member = me ?? assigned[0]
  const s = (member?.status ?? meeting.status ?? '').trim().toLowerCase()
  const hasActionDate = member?.action_date != null && member.action_date !== ''
  if (s === 'performed') return 'attended'
  if ((s === 'accept' || s === 'accepted') && hasActionDate) return 'missed'
  return 'notAttending'
}

/** Sort meetings by created date descending (latest created first) */
function sortMeetingsByDateDesc(meetings: Meeting[]): Meeting[] {
  return meetings.sort((a, b) => {
    const dateA = new Date(a.created_date || a.start_date).getTime()
    const dateB = new Date(b.created_date || b.start_date).getTime()
    return dateB - dateA
  })
}

function groupByPendingColumn(meetings: Meeting[], userId: number | undefined): Record<PendingColumnId, Meeting[]> {
  const columns: Record<PendingColumnId, Meeting[]> = {
    noAction: [],
    attending: [],
    notAttending: [],
    attended: [],
  }
  for (const meeting of meetings) {
    columns[getPendingColumnId(meeting, userId)].push(meeting)
  }
  for (const key of Object.keys(columns) as PendingColumnId[]) {
    sortMeetingsByDateDesc(columns[key])
  }
  return columns
}

function groupByCompletedColumn(meetings: Meeting[], userId: number | undefined): Record<CompletedColumnId, Meeting[]> {
  const columns: Record<CompletedColumnId, Meeting[]> = {
    attended: [],
    missed: [],
    notAttending: [],
  }
  for (const meeting of meetings) {
    columns[getCompletedColumnId(meeting, userId)].push(meeting)
  }
  for (const key of Object.keys(columns) as CompletedColumnId[]) {
    sortMeetingsByDateDesc(columns[key])
  }
  return columns
}

function formatMeetingDate(dateStr: string | undefined | null): string | undefined {
  if (!dateStr?.trim()) return undefined
  return dateStr.trim().split(/\s+/)[0] ?? undefined
}

function MeetingsPage() {
  const user = useAuthStore((s) => s.user)
  const userId = user?.id
  const queryClient = useQueryClient()
  // Meeting cards render here, so revalidate the meeting-mute cache on this
  // route (hydration is route-scoped, not global — see (app)/layout.tsx).
  useMeetingMuteHydrate()
  const optimisticLockRef = React.useRef(false)
  const requestOptimisticLock = React.useCallback(() => {
    optimisticLockRef.current = true
  }, [])

  // Deep-link handler for FCM `auto_meeting_call` notifications. The service
  // worker (`src/lib/firebase/sw.ts`) routes a notification click to
  // `/productivity/meetings?meetingId=<id>`; opening the details sheet on
  // arrival surfaces the Join Video Call button so the user can finish the
  // dial. Runs once on mount — the URL query param is consumed by the
  // notification click handler, not by in-app navigation.
  React.useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const raw = params.get('meetingId')
    if (raw == null) return
    const id = Number(raw)
    if (Number.isFinite(id)) {
      meetingStore.getState().setSelectedMeetingId(id)
    }
    // Strip the param so a back-nav / reload doesn't re-open the sheet.
    params.delete('meetingId')
    const qs = params.toString()
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${qs ? `?${qs}` : ''}`,
    )
  }, [])

  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('pending')
  const [searchQuery, setSearchQuery] = React.useState('')
  // Date filters (pending tab) — declared here (before `filteredMeetings`) since
  // that memo reads them, so the donut + boards stay in sync. Today / Upcoming
  // narrow which meetings show by start date; cards still land in their real
  // status column. The dropdown UI lives in the MeetingsContent child, which
  // receives `dateFilter` + `toggleDateFilter` as props.
  const [dateFilter, setDateFilter] = React.useState<Set<'today' | 'upcoming'>>(new Set())
  React.useEffect(() => {
    setDateFilter(new Set())
  }, [statusFilter])
  const toggleDateFilter = React.useCallback((key: 'today' | 'upcoming') => {
    setDateFilter((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])
  const [pendingColumns, setPendingColumns] = React.useState<Record<string, Meeting[]>>({
    noAction: [],
    attending: [],
    notAttending: [],
    attended: [],
  })
  const [completedColumns, setCompletedColumns] = React.useState<Record<string, Meeting[]>>({
    attended: [],
    missed: [],
    notAttending: [],
  })

  const storeMeetings = useMeetingStore((s) => s.meetings)
  const { isConnected: socketConnected } = useSocket()

  const meetingListQuery = useQuery({
    queryKey: queryKeys.meeting.list({
      pagination: 'Y',
      page_limit: 500,
      page_no: 1,
    }),
    queryFn: async () => {
      const { lastSyncTime, meetings: cached, setMeetings, mergeMeetings, setLastSyncTime } = meetingStore.getState()
      const hasCachedList = cached.length > 0
      const syncTime = lastSyncTime > 0 && hasCachedList ? lastSyncTime : 0
      const res = await getMeetingList({
        pagination: 'Y',
        page_limit: 500,
        page_no: 1,
        last_sync_time: syncTime,
      })

      const data = res.data ?? []
      if (syncTime === 0) {
        setMeetings(reinjectPendingOptimisticMeetings(data))
      } else {
        const protectedData = filterOutPendingOptimisticMeetings(data)
        if (protectedData.length > 0) mergeMeetings(protectedData)
      }

      const serverSync = res.last_sync_time ?? 0
      const latestFromRecords = data.length > 0
        ? Math.max(...data.map((m) => Number(m.edited_time) || 0))
        : 0
      const nextSync = Math.max(0, Math.max(latestFromRecords, serverSync) - 10_000)
      if (nextSync !== lastSyncTime) {
        setLastSyncTime(nextSync)
      }

      return meetingStore.getState().meetings
    },
    select: (data: Meeting[]) =>
      pendingOptimisticMeetingIds.size > 0
        ? data.filter((m) => !pendingOptimisticMeetingIds.has(m.id))
        : data,
    // Poll only as a fallback when the socket is down. When connected,
    // `useMeetingSocketSync` keeps the list live (real-time), so no polling is
    // needed — `false` schedules no refetch timer at all. A disconnect flips
    // `socketConnected`, re-renders, and re-enables the 10s fallback.
    refetchInterval: () =>
      pendingOptimisticMeetingIds.size > 0 || socketConnected ? false : 10_000,
  })

  React.useEffect(() => {
    const unsub = subscribeMeetingListSync(() => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.meeting.list({ pagination: 'Y', page_limit: 500, page_no: 1 }),
      })
    })
    return unsub
  }, [queryClient])

  const allMeetings = React.useMemo(
    () =>
      storeMeetings.length > 0
        ? storeMeetings
        : Array.isArray(meetingListQuery.data)
          ? meetingListQuery.data
          : [],
    [storeMeetings, meetingListQuery.data],
  )
  const isLoading = meetingListQuery.isLoading
  const isError = meetingListQuery.isError
  const error = meetingListQuery.error

  const filteredMeetings = React.useMemo(() => {
    let list: Meeting[] = Array.isArray(allMeetings) ? allMeetings : []

    if (statusFilter === 'pending') {
      list = list.filter((m) => m.is_completed === 0)
    }
    if (statusFilter === 'completed') {
      list = list.filter((m) => m.is_completed === 1)
    }

    // Date filter (Today / Upcoming) — narrows by start date regardless of status.
    if (dateFilter.size > 0) {
      list = list.filter((m) => {
        const bucket = meetingDayBucket(m.start_date)
        return bucket === 'today' || bucket === 'upcoming'
          ? dateFilter.has(bucket)
          : false
      })
    }

    const q = searchQuery.trim().toLowerCase()
    if (q) {
      list = list.filter((m) => (m.title ?? '').toLowerCase().includes(q))
    }

    return sortMeetingsByDateDesc([...list])
  }, [allMeetings, statusFilter, searchQuery, dateFilter])

  const pendingCount = React.useMemo(
    () => allMeetings.filter((m) => m.is_completed === 0).length,
    [allMeetings],
  )
  const completedCount = React.useMemo(
    () => allMeetings.filter((m) => m.is_completed === 1).length,
    [allMeetings],
  )

  const pendingDistribution = React.useMemo(() => {
    const g = groupByPendingColumn(filteredMeetings, userId)
    return {
      noAction: g.noAction.length,
      attending: g.attending.length,
      notAttending: g.notAttending.length,
      attended: g.attended.length,
      total: filteredMeetings.length,
    }
  }, [filteredMeetings, userId])

  const completedDistribution = React.useMemo(() => {
    const g = groupByCompletedColumn(filteredMeetings, userId)
    return {
      attended: g.attended.length,
      missed: g.missed.length,
      notAttending: g.notAttending.length,
      total: filteredMeetings.length,
    }
  }, [filteredMeetings, userId])

  React.useEffect(() => {
    if (optimisticLockRef.current) {
      optimisticLockRef.current = false
      return
    }
    if (filteredMeetings.length === 0 && !isLoading) {
      setPendingColumns({ noAction: [], attending: [], notAttending: [], attended: [] })
      setCompletedColumns({ attended: [], missed: [], notAttending: [] })
      return
    }
    if (filteredMeetings.length > 0) {
      setPendingColumns(groupByPendingColumn(filteredMeetings, userId))
      setCompletedColumns(groupByCompletedColumn(filteredMeetings, userId))
    }
  }, [filteredMeetings, isLoading, userId])

  const handlePendingChange = React.useCallback((newColumns: Record<string, Meeting[]>) => {
    setPendingColumns(newColumns)
  }, [])

  const handleCompletedChange = React.useCallback((newColumns: Record<string, Meeting[]>) => {
    setCompletedColumns(newColumns)
  }, [])

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6 h-[calc(100vh-3rem)] -mt-2">
        <div className="flex items-center justify-center min-h-[320px]">
          <div className="flex flex-col items-center gap-3 text-muted-foreground">
            <Loader2 className="size-8 animate-spin" />
            <p className="text-sm">Loading meetings...</p>
          </div>
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex flex-col gap-6 h-[calc(100vh-3rem)] -mt-2">
        <div className="flex items-center justify-center min-h-[320px]">
          <div className="text-center text-destructive">
            <p className="font-medium">Failed to load meetings</p>
            <p className="text-sm text-muted-foreground mt-1">
              {error instanceof Error ? error.message : 'Something went wrong'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6 -mt-2 xl:h-[calc(100vh-0.5rem)] xl:overflow-hidden">
      {/* Row: Pending, Completed (left) + Search (right) */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="inline-flex rounded-lg bg-muted p-1 gap-0.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setStatusFilter('pending')}
            className={cn(
              'rounded-md font-semibold text-foreground hover:bg-background/80',
              statusFilter === 'pending' && 'bg-background shadow-sm',
            )}
          >
            Pending
            {statusFilter === 'pending' ? (
              <span className="ml-1.5 inline-flex items-center justify-center rounded-md bg-white px-1.5 py-0.5 text-xs font-semibold text-foreground shadow-sm ring-1 ring-border/50 dark:bg-muted">
                {pendingCount}
              </span>
            ) : (
              <span className="ml-1.5 text-sm font-semibold text-foreground tabular-nums">
                {pendingCount}
              </span>
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setStatusFilter('completed')}
            className={cn(
              'rounded-md font-semibold text-foreground hover:bg-background/80',
              statusFilter === 'completed' && 'bg-background shadow-sm',
            )}
          >
            Completed
            {statusFilter === 'completed' ? (
              <span className="ml-1.5 inline-flex items-center justify-center rounded-md bg-white px-1.5 py-0.5 text-xs font-semibold text-foreground shadow-sm ring-1 ring-border/50 dark:bg-muted">
                {completedCount}
              </span>
            ) : (
              <span className="ml-1.5 text-sm font-semibold text-foreground tabular-nums">
                {completedCount}
              </span>
            )}
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-96 min-w-[200px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              placeholder="Search meetings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 rounded-lg"
            />
          </div>
          <CreateMeetingDialog />
        </div>
      </div>

      <MeetingsContent
        pendingColumns={pendingColumns}
        onPendingChange={handlePendingChange}
        completedColumns={completedColumns}
        onCompletedChange={handleCompletedChange}
        pendingDistribution={pendingDistribution}
        completedDistribution={completedDistribution}
        statusFilter={statusFilter}
        userId={userId}
        filteredMeetings={filteredMeetings}
        dateFilter={dateFilter}
        toggleDateFilter={toggleDateFilter}
        requestOptimisticLock={requestOptimisticLock}
      />
    </div>
  )
}

interface MeetingsContentProps {
  pendingColumns: Record<string, Meeting[]>
  onPendingChange: (cols: Record<string, Meeting[]>) => void
  completedColumns: Record<string, Meeting[]>
  onCompletedChange: (cols: Record<string, Meeting[]>) => void
  pendingDistribution: {
    noAction: number
    attending: number
    notAttending: number
    attended: number
    total: number
  }
  completedDistribution: {
    attended: number
    missed: number
    notAttending: number
    total: number
  }
  statusFilter: StatusFilter
  userId: number | undefined
  filteredMeetings: Meeting[]
  /** Date filter (pending tab) — owned by the parent so the donut + boards match. */
  dateFilter: Set<'today' | 'upcoming'>
  toggleDateFilter: (key: 'today' | 'upcoming') => void
  /** Tells the parent to skip the next server-driven layout sync. */
  requestOptimisticLock: () => void
}

function MeetingsContent({
  pendingColumns,
  onPendingChange,
  completedColumns,
  onCompletedChange,
  pendingDistribution,
  completedDistribution,
  statusFilter,
  userId,
  filteredMeetings,
  dateFilter,
  toggleDateFilter,
  requestOptimisticLock,
}: MeetingsContentProps) {
  const queryClient = useQueryClient()
  const isCompletedBoard = statusFilter === 'completed'
  const dist = isCompletedBoard ? completedDistribution : pendingDistribution
  const total = dist.total
  const meetingUnreadCounts = useMeetingUnreadCounts()

  const [viewMode, setViewMode] = React.useState<'board' | 'table'>('board')

  // ——— Table-view row selection + bulk actions ———
  const [selectedIds, setSelectedIds] = React.useState<Set<number>>(new Set())
  const [bulkAction, setBulkAction] = React.useState('')
  const [bulkBusy, setBulkBusy] = React.useState(false)
  const [activeColumnFilters, setActiveColumnFilters] = React.useState<Set<string>>(new Set())

  React.useEffect(() => {
    setActiveColumnFilters(new Set())
  }, [statusFilter])

  const toggleColumnFilter = React.useCallback((col: string) => {
    setActiveColumnFilters((prev) => {
      const next = new Set(prev)
      if (next.has(col)) {
        next.delete(col)
      } else {
        next.add(col)
      }
      return next
    })
  }, [])

  // Column-visibility filter (status columns). The date filter (Today/Upcoming)
  // is applied upstream on `filteredMeetings` in the parent, so it must NOT feed
  // this flag (else an empty column set would blank every column).
  const hasFilter = activeColumnFilters.size > 0
  const activeFilterCount = activeColumnFilters.size + dateFilter.size

  // ── DnD: refs that callbacks read at fire-time without rebinding ──
  const pendingColumnsRef = React.useRef(pendingColumns)
  const filteredMeetingsRef = React.useRef(filteredMeetings)
  const statusFilterRef = React.useRef(statusFilter)
  const userIdRef = React.useRef(userId)
  const onPendingChangeRef = React.useRef(onPendingChange)
  const onCompletedChangeRef = React.useRef(onCompletedChange)
  const dragMeetingIdRef = React.useRef<number | null>(null)
  const dragSourceColumnRef = React.useRef<PendingMeetingColumnId | null>(null)
  const optimisticOriginalRef = React.useRef<Meeting | null>(null)

  React.useEffect(() => {
    pendingColumnsRef.current = pendingColumns
    filteredMeetingsRef.current = filteredMeetings
    statusFilterRef.current = statusFilter
    userIdRef.current = userId
    onPendingChangeRef.current = onPendingChange
    onCompletedChangeRef.current = onCompletedChange
  }, [pendingColumns, filteredMeetings, statusFilter, userId, onPendingChange, onCompletedChange])

  const { mutate: acceptRejectMutate } = useMutation({
    mutationFn: meetingAcceptReject,
    onSuccess: (data, variables) => {
      // meeting-accept-reject returns {code: 200, message, data}; apiPost throws on non-200,
      // so reaching here is itself success — code check is just a defensive belt-and-braces.
      const ok = data.code === 200 || data.status === true
      if (ok) {
        optimisticOriginalRef.current = null
        toast.success(data.message ?? 'Meeting updated.')
        notifyMeetingListSyncFromMutation({ meeting_id: variables.meeting_id })
        setTimeout(async () => {
          await queryClient.invalidateQueries({
            queryKey: queryKeys.meeting.list({ pagination: 'Y', page_limit: 500, page_no: 1 }),
          })
          pendingOptimisticMeetingIds.delete(variables.meeting_id)
        }, 15_000)
      } else {
        pendingOptimisticMeetingIds.delete(variables.meeting_id)
        toast.error(data.message ?? 'Something went wrong!')
        rollbackOptimisticMove(variables.meeting_id)
      }
    },
    onError: (_err, variables) => {
      pendingOptimisticMeetingIds.delete(variables.meeting_id)
      toast.error('Something went wrong!')
      rollbackOptimisticMove(variables.meeting_id)
    },
  })

  /**
   * Rolls back an optimistic drag when the server rejects the status change.
   *
   * The earlier implementation called
   * `groupByPendingColumn(filteredMeetingsRef.current, …)` here — but
   * `filteredMeetingsRef.current` is set in a `useEffect` that only fires
   * after the next render. We've just merged the original meeting back into
   * the store in this same tick, so the ref is one render stale and still
   * holds the *optimistic* meeting. Result: the rebuild left the card in
   * the column the user dragged it to, even though the badge had reverted.
   *
   * Read the meetings list directly from the store (we just wrote it) and
   * filter by status to get the fresh `pending` set, then re-group.
   */
  function rollbackOptimisticMove(meetingId: number) {
    const orig = optimisticOriginalRef.current
    optimisticOriginalRef.current = null
    if (orig) meetingStore.getState().mergeMeetings([orig])
    const freshPending = meetingStore
      .getState()
      .meetings.filter((m) => m.is_completed === 0)
    onPendingChangeRef.current(groupByPendingColumn(freshPending, userIdRef.current))
    void meetingId
  }

  const handlePendingDragStart = React.useCallback((event: DragStartEvent) => {
    const meetingId = event.active.id as number
    dragMeetingIdRef.current = meetingId
    dragSourceColumnRef.current = findMeetingColumn(meetingId, pendingColumnsRef.current)
  }, [])

  const handlePendingDragEnd = React.useCallback((event: DragEndEvent) => {
    const sourceCol = dragSourceColumnRef.current
    const draggedMeetingId = dragMeetingIdRef.current
    dragMeetingIdRef.current = null
    dragSourceColumnRef.current = null

    if (statusFilterRef.current !== 'pending') return
    if (!sourceCol || draggedMeetingId == null) return

    const { over } = event
    if (!over) return

    const cols = pendingColumnsRef.current
    let targetCol: PendingMeetingColumnId | null = null
    const overKey = over.id as string | number
    if (typeof overKey === 'string' && overKey in cols) {
      targetCol = overKey as PendingMeetingColumnId
    } else {
      targetCol = findMeetingColumn(over.id as number, cols)
    }

    if (!targetCol || targetCol === sourceCol) return

    // Look up the meeting from filteredMeetings since the kanban's onDragOver
    // has already moved it out of cols[sourceCol] mid-drag.
    const meeting = filteredMeetingsRef.current.find((m) => m.id === draggedMeetingId)
    if (!meeting) return

    const uid = userIdRef.current
    const status = resolveAcceptRejectForMeetingMove(sourceCol, targetCol, meeting)
    if (status == null) {
      toast.error('This move is not allowed.')
      onPendingChangeRef.current(groupByPendingColumn(filteredMeetingsRef.current, uid))
      return
    }

    // Role gate: `meeting-accept-reject` adjusts the CURRENT user's RSVP.
    // Creators of a delegated meeting (their member row has `is_assigned: 0`)
    // can't legitimately change the assignee's status — the API returns
    // "Record not found" for that pair. Match iOS/Android, which hide the
    // accept-reject buttons entirely for non-assignees and offer the
    // creator their own `meeting-status-update` (Completed / Pending) action
    // from the details sheet instead. The drag stays alive so the affordance
    // doesn't disappear, but we reject the drop and explain why.
    if (!canAcceptRejectMeeting(meeting, uid)) {
      toast.error('Only the assignee can change their attendance status')
      onPendingChangeRef.current(groupByPendingColumn(filteredMeetingsRef.current, uid))
      return
    }

    pendingOptimisticMeetingIds.add(meeting.id)

    if (uid != null) {
      const { patched, original } = applyOptimisticMeetingStatus(meeting, status, uid)
      optimisticOriginalRef.current = original
      meetingStore.getState().mergeMeetings([patched])

      const updatedFiltered = filteredMeetingsRef.current.map((m) => (m.id === patched.id ? patched : m))
      const rebuilt = groupByPendingColumn(updatedFiltered, uid)
      requestOptimisticLock()
      onPendingChangeRef.current(rebuilt)
    }

    acceptRejectMutate({ meeting_id: meeting.id, status })
  }, [acceptRejectMutate, requestOptimisticLock])

  const handlePendingDragCancel = React.useCallback(() => {
    dragMeetingIdRef.current = null
    dragSourceColumnRef.current = null
  }, [])

  const handleCompletedDragStart = React.useCallback((event: DragStartEvent) => {
    event.activatorEvent.preventDefault()
    toast.info('You cannot take this action.')
  }, [])

  const handleCompletedDragEnd = React.useCallback(() => {
    if (statusFilterRef.current !== 'completed') return
    onCompletedChangeRef.current(groupByCompletedColumn(filteredMeetingsRef.current, userIdRef.current))
  }, [])

  const handleCompletedDragCancel = React.useCallback(() => {
    if (statusFilterRef.current !== 'completed') return
    onCompletedChangeRef.current(groupByCompletedColumn(filteredMeetingsRef.current, userIdRef.current))
  }, [])

  const filteredPendingColumns = React.useMemo(() => {
    if (!hasFilter) return pendingColumns
    const result: Record<string, Meeting[]> = {}
    for (const key of PENDING_COLUMNS) {
      result[key] = activeColumnFilters.has(key) ? (pendingColumns[key] ?? []) : []
    }
    return result
  }, [pendingColumns, hasFilter, activeColumnFilters])

  const filteredCompletedColumns = React.useMemo(() => {
    if (!hasFilter) return completedColumns
    const result: Record<string, Meeting[]> = {}
    for (const key of COMPLETED_COLUMNS) {
      result[key] = activeColumnFilters.has(key) ? (completedColumns[key] ?? []) : []
    }
    return result
  }, [completedColumns, hasFilter, activeColumnFilters])

  const flatMeetings = React.useMemo(() => {
    if (isCompletedBoard) {
      return [
        ...(filteredCompletedColumns.attended ?? []),
        ...(filteredCompletedColumns.missed ?? []),
        ...(filteredCompletedColumns.notAttending ?? []),
      ]
    }
    return [
      ...(filteredPendingColumns.noAction ?? []),
      ...(filteredPendingColumns.attending ?? []),
      ...(filteredPendingColumns.notAttending ?? []),
      ...(filteredPendingColumns.attended ?? []),
    ]
  }, [isCompletedBoard, filteredPendingColumns, filteredCompletedColumns])

  // Selection is scoped to the rows currently visible in the table.
  const visibleIds = React.useMemo(() => flatMeetings.map((m) => m.id), [flatMeetings])
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id))
  const someSelected = selectedIds.size > 0 && !allSelected

  // Drop any selected ids that scrolled out of the active filter/view so a bulk
  // action can never touch a hidden row.
  React.useEffect(() => {
    setSelectedIds((prev) => {
      const next = new Set([...prev].filter((id) => visibleIds.includes(id)))
      return next.size === prev.size ? prev : next
    })
  }, [visibleIds])

  const toggleRow = React.useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const toggleAll = React.useCallback(() => {
    setSelectedIds(allSelected ? new Set<number>() : new Set(visibleIds))
  }, [allSelected, visibleIds])

  const clearSelection = React.useCallback(() => {
    setSelectedIds(new Set<number>())
    setBulkAction('')
  }, [])

  // Bulk handler — applies to eligible rows, skips the rest with a summary toast.
  // Pin/Unpin: local-only (mobile parity, no server sync). Mute/Unmute: optimistic
  // cache + server mute-management. Delete: creator-only + occurrence-only (whole
  // recurring series deletion stays in the details sheet).
  async function applyBulk() {
    const sel = flatMeetings.filter((m) => selectedIds.has(m.id))
    if (sel.length === 0 || !bulkAction) return
    setBulkBusy(true)
    try {
      if (bulkAction === 'pin' || bulkAction === 'unpin') {
        const want = bulkAction === 'pin'
        const pin = useMeetingPinStore.getState()
        resolvePinToggles(sel, pin.isPinned, want).forEach((id) => pin.togglePin(id))
        toast.success(`${sel.length} meeting${sel.length !== 1 ? 's' : ''} ${want ? 'pinned' : 'unpinned'}`)
      } else if (bulkAction === 'mute' || bulkAction === 'unmute') {
        const muted = bulkAction === 'mute'
        const mute = useMeetingMuteStore.getState()
        sel.forEach((m) => mute.setMuted(m.id, muted))
        const results = await Promise.allSettled(
          sel.map((m) => setMeetingMute({ meetingId: m.id, muted })),
        )
        const failed = results.filter((r) => r.status === 'rejected').length
        toast.success(
          `${sel.length - failed} meeting${sel.length - failed !== 1 ? 's' : ''} ${muted ? 'muted' : 'unmuted'}` +
            (failed ? ` · ${failed} failed` : ''),
        )
      } else if (bulkAction === 'delete') {
        const { deletable: mine, skipped } = partitionDeletableMeetings(sel, userId)
        const results = await Promise.allSettled(mine.map((m) => deleteMeetingById(m.id)))
        const okIds = mine
          .filter((_, i) => results[i].status === 'fulfilled')
          .map((m) => m.id)
        if (okIds.length > 0) {
          const store = meetingStore.getState()
          store.setMeetings(store.meetings.filter((m) => !okIds.includes(m.id)))
        }
        toast.success(
          `${okIds.length} deleted` + (skipped ? ` · ${skipped} skipped (not yours)` : ''),
        )
      }
      clearSelection()
      await queryClient.invalidateQueries({
        queryKey: queryKeys.meeting.list({ pagination: 'Y', page_limit: 500, page_no: 1 }),
      })
    } finally {
      setBulkBusy(false)
    }
  }

  function getMeetingStatusLabel(meeting: Meeting): string {
    if (isCompletedBoard) {
      const key = (['attended', 'notAttending'] as const).find(
        (k) => (completedColumns[k] ?? []).some((m) => m.id === meeting.id),
      )
      return key ? COMPLETED_TITLES[key] : '—'
    }
    const event = mapMeetingToEvent(meeting, userId ?? undefined)
    return event.meetingStatusDisplay ?? 'No Action'
  }

  type LegendItem = { label: string; count: number; colorClass: string; fill: string }
  const legendItems: LegendItem[] = isCompletedBoard
    ? [
        { label: 'Attended', count: completedDistribution.attended, colorClass: 'bg-blue-600', fill: '#2563eb' },
        { label: 'Missed', count: completedDistribution.missed, colorClass: 'bg-amber-500', fill: '#f59e0b' },
        { label: 'Not Attended', count: completedDistribution.notAttending, colorClass: 'bg-destructive', fill: '#dc2626' },
      ]
    : [
        { label: 'No Action', count: pendingDistribution.noAction, colorClass: 'bg-slate-500', fill: '#64748b' },
        { label: 'Attending', count: pendingDistribution.attending, colorClass: 'bg-green-600', fill: '#16a34a' },
        { label: 'Not Attending', count: pendingDistribution.notAttending, colorClass: 'bg-destructive', fill: '#dc2626' },
        { label: 'Attended', count: pendingDistribution.attended, colorClass: 'bg-blue-600', fill: '#2563eb' },
      ]

  const pieData = legendItems
    .filter((item) => item.count > 0)
    .map((item) => ({
      name: item.label,
      value: item.count,
      fill: item.fill,
    }))

  const RADIAN = Math.PI / 180
  const innerR = 44
  const outerR = 64
  const labelRadius = (innerR + outerR) / 2
  const renderPieSegmentLabel = (props: {
    cx?: number
    cy?: number
    midAngle?: number
    value?: number
    payload?: { value?: number }
  }) => {
    const { cx = 0, cy = 0, midAngle = 0, value, payload } = props
    const count = payload?.value ?? value
    if (count == null || count === 0) return null
    const x = cx + labelRadius * Math.cos(-midAngle * RADIAN)
    const y = cy + labelRadius * Math.sin(-midAngle * RADIAN)
    return (
      <text
        x={x}
        y={y}
        fill="currentColor"
        textAnchor="middle"
        dominantBaseline="central"
        className="text-[10px] font-semibold tabular-nums text-foreground"
        style={{ pointerEvents: 'none' }}
      >
        {count}
      </text>
    )
  }

  const chartConfig: ChartConfig = legendItems.reduce(
    (acc, item) => ({ ...acc, [item.label]: { label: item.label, color: item.fill } }),
    {} as ChartConfig,
  )

  return (
    <>
      <Card
        className={cn(
          'py-3 mt-5',
          statusFilter === 'completed'
            ? 'bg-gradient-to-br from-violet-50 to-violet-100/50 dark:from-violet-950/30 dark:to-violet-900/20 border border-violet-200/80 dark:border-violet-800/50'
            : 'bg-gradient-to-br from-rose-50 to-rose-100/50 dark:from-rose-950/30 dark:to-rose-900/20 border border-rose-200/80 dark:border-rose-800/50',
        )}
      >
        <CardContent className="pt-0">
          <div className="flex gap-6 items-stretch">
            <div className="relative shrink-0 w-[140px] h-[140px]">
              {pieData.length > 0 ? (
                <>
                  <ChartContainer config={chartConfig} className="absolute inset-0 h-[140px] w-[140px]">
                    <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                      <ChartTooltip
                        content={({ active, payload }) => {
                          if (!active || !payload?.[0]?.payload || total === 0) return null
                          const p = payload[0].payload as { name: string; value: number }
                          const pct = Math.round((p.value / total) * 100)
                          return (
                            <div className="rounded-lg border bg-background px-3 py-2 shadow-sm text-sm">
                              {p.name}: {p.value} meeting{p.value !== 1 ? 's' : ''} ({pct}%)
                            </div>
                          )
                        }}
                      />
                      <Pie
                        data={pieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={innerR}
                        outerRadius={outerR}
                        paddingAngle={2}
                        strokeWidth={0}
                        labelLine={false}
                        label={renderPieSegmentLabel}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ChartContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-10">
                    <span className="text-2xl font-bold tabular-nums text-foreground">{total}</span>
                    <span className="text-xs text-muted-foreground">Total</span>
                  </div>
                </>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground">
                  <span className="text-2xl font-bold tabular-nums">{total}</span>
                  <span className="text-xs">Total</span>
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0 flex flex-col">
              <div className="flex items-center gap-2 flex-wrap pb-4">
                <CardTitle className="text-base font-semibold text-foreground shrink-0">
                  {isCompletedBoard
                    ? 'Meeting Distribution'
                    : `Total meetings (${total})`}
                </CardTitle>
                {isCompletedBoard && (
                  <p className="text-sm text-muted-foreground">
                    Total {total} meeting{total !== 1 ? 's' : ''} across all columns
                  </p>
                )}
              </div>
              <div className="space-y-3">
                {legendItems.map((item) => {
                  const pct = total > 0 ? (item.count / total) * 100 : 0
                  return (
                    <div
                      key={item.label}
                      className="flex items-center gap-3 w-full min-w-0"
                    >
                      <div
                        className="size-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: item.fill }}
                        aria-hidden
                      />
                      <span className="text-sm font-medium text-foreground shrink-0 w-24">
                        {item.label}
                      </span>
                      <div
                        className={cn(
                          'flex-1 min-w-0 h-2 overflow-hidden rounded-full',
                          statusFilter === 'completed'
                            ? 'bg-[#E6E2F9] dark:bg-[#3b3648]'
                            : 'bg-[#F6DCDE] dark:bg-[#453638]',
                        )}
                      >
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${pct}%`, backgroundColor: item.fill }}
                        />
                      </div>
                      <span className="text-sm text-foreground tabular-nums shrink-0 w-10 text-right font-medium">
                        {item.count}/{total}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Board + Filters */}
      <div className="flex items-center justify-end gap-2 py-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              {viewMode === 'board' && <LayoutGrid className="size-4 mr-1" />}
              {viewMode === 'table' && <TableIcon className="size-4 mr-1" />}
              {viewMode === 'board' && 'Board'}
              {viewMode === 'table' && 'Table'}
              <ChevronDown className="size-4 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setViewMode('board')}>
              <LayoutGrid className="size-4 mr-2" />
              Board
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setViewMode('table')}>
              <TableIcon className="size-4 mr-2" />
              Table
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Filter className="size-4 mr-1" />
              Filters
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="ml-1 rounded-full px-1.5 py-0 text-[10px]">
                  {activeFilterCount}
                </Badge>
              )}
              <ChevronDown className="size-4 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {isCompletedBoard
              ? COMPLETED_COLUMNS.map((col) => (
                  <DropdownMenuCheckboxItem
                    key={col}
                    checked={activeColumnFilters.has(col)}
                    onCheckedChange={() => toggleColumnFilter(col)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {COMPLETED_TITLES[col]}
                  </DropdownMenuCheckboxItem>
                ))
              : (
                  <>
                    {PENDING_COLUMNS.map((col) => (
                      <DropdownMenuCheckboxItem
                        key={col}
                        checked={activeColumnFilters.has(col)}
                        onCheckedChange={() => toggleColumnFilter(col)}
                        onSelect={(e) => e.preventDefault()}
                      >
                        {PENDING_TITLES[col]}
                      </DropdownMenuCheckboxItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-[11px] font-medium text-muted-foreground">
                      Date
                    </DropdownMenuLabel>
                    <DropdownMenuCheckboxItem
                      checked={dateFilter.has('today')}
                      onCheckedChange={() => toggleDateFilter('today')}
                      onSelect={(e) => e.preventDefault()}
                    >
                      Today
                    </DropdownMenuCheckboxItem>
                    <DropdownMenuCheckboxItem
                      checked={dateFilter.has('upcoming')}
                      onCheckedChange={() => toggleDateFilter('upcoming')}
                      onSelect={(e) => e.preventDefault()}
                    >
                      Upcoming
                    </DropdownMenuCheckboxItem>
                  </>
                )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex flex-col xl:flex-1 xl:min-h-0 xl:overflow-hidden">
        {viewMode === 'table' ? (
          <div className="flex flex-1 min-h-0 flex-col gap-3">
            {/* Bulk actions bar — only visible once at least one row is selected. */}
            {selectedIds.size > 0 && (
              <div className="flex items-center gap-3 rounded-lg border bg-muted/30 p-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                    onCheckedChange={toggleAll}
                    aria-label="Select all"
                  />
                  <span className="text-sm text-muted-foreground">{selectedIds.size} selected</span>
                </div>
                <div className="h-4 w-px bg-border" />
                <div className="flex flex-1 items-center gap-2">
                  <Select value={bulkAction} onValueChange={setBulkAction}>
                    <SelectTrigger className="h-9 w-[160px]">
                      <SelectValue placeholder="Bulk Actions" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pin">Pin</SelectItem>
                      <SelectItem value="unpin">Unpin</SelectItem>
                      <SelectItem value="mute">Mute</SelectItem>
                      <SelectItem value="unmute">Unmute</SelectItem>
                      <SelectItem value="delete">Delete</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" onClick={applyBulk} disabled={!bulkAction || bulkBusy}>
                    {bulkBusy ? (
                      <>
                        <Loader2 className="size-4 mr-1 animate-spin" />
                        Applying…
                      </>
                    ) : (
                      'Apply'
                    )}
                  </Button>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearSelection}
                  className="text-muted-foreground"
                >
                  Clear Selection
                </Button>
              </div>
            )}
            <div className="flex-1 min-h-0 overflow-auto rounded-md border">
            <Table>
              <TableHeader className="[&_tr]:border-b bg-muted sticky top-0 z-10">
                <TableRow>
                  <TableHead className="sticky left-0 z-30 w-[44px] bg-muted">
                    <Checkbox
                      checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                      onCheckedChange={toggleAll}
                      aria-label="Select all"
                    />
                  </TableHead>
                  <TableHead className="sticky left-[44px] bg-muted z-20 min-w-[250px]">Meeting</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="min-w-[180px]">Attendees</TableHead>
                  <TableHead>Tag</TableHead>
                  <TableHead className="w-[64px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {flatMeetings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                      No meetings
                    </TableCell>
                  </TableRow>
                ) : (
                  flatMeetings.map((meeting) => {
                    const assignees = getMeetingAssignees(meeting)
                    const statusLabel = getMeetingStatusLabel(meeting)
                    const meetingDate = formatMeetingDate(meeting.start_date)
                    const setSelectedMeetingId = useMeetingStore.getState().setSelectedMeetingId
                    const isRowSelected = selectedIds.has(meeting.id)
                    return (
                      <TableRow
                        key={meeting.id}
                        data-state={isRowSelected ? 'selected' : undefined}
                        className="cursor-pointer transition-colors hover:bg-muted/50"
                        onClick={() => setSelectedMeetingId(meeting.id)}
                      >
                        <TableCell
                          className="sticky left-0 z-20 w-[44px] bg-background"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            checked={isRowSelected}
                            onCheckedChange={() => toggleRow(meeting.id)}
                            aria-label={`Select ${meeting.title ?? 'meeting'}`}
                          />
                        </TableCell>
                        <TableCell className="sticky left-[44px] bg-background z-10">
                          <div className="flex items-center gap-3">
                            <Avatar className="size-9 border border-border">
                              <AvatarImage
                                src={meeting.receiver?.user_image || meeting.creator?.user_image || undefined}
                                alt={meeting.title ?? 'Meeting'}
                                className="object-cover"
                              />
                              <AvatarFallback className="bg-rose-100 text-rose-700">
                                {getInitials(meeting.title ?? 'M')}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex flex-col">
                              <div className="flex items-center gap-1.5">
                                <span className="font-medium leading-tight text-foreground">
                                  {meeting.title ?? '—'}
                                </span>
                                {(meetingUnreadCounts.get(meeting.id) ?? 0) > 0 && (
                                  <span className="flex size-5 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white shrink-0 dark:bg-blue-500">
                                    {(meetingUnreadCounts.get(meeting.id) ?? 0) > 9 ? '9+' : meetingUnreadCounts.get(meeting.id)}
                                  </span>
                                )}
                              </div>
                              <span className="text-xs text-muted-foreground">
                                {meeting.creator?.user_name ? `Created by ${meeting.creator.user_name}` : '—'}
                              </span>
                            </div>
                          </div>
                        </TableCell>

                        <TableCell>
                          <Badge
                            variant="outline"
                            className={cn(
                              statusLabel === 'Attending' && 'bg-green-500/10 text-green-600 border-green-500/20',
                              statusLabel === 'No Action' && 'bg-slate-500/10 text-slate-600 border-slate-500/20',
                              statusLabel === 'Not Attending' && 'bg-red-500/10 text-red-600 border-red-500/20',
                              statusLabel === 'Done' && 'bg-blue-500/10 text-blue-600 border-blue-500/20',
                              statusLabel === 'Attended' && 'bg-blue-500/10 text-blue-600 border-blue-500/20',
                            )}
                          >
                            {statusLabel}
                          </Badge>
                        </TableCell>

                        <TableCell className="text-sm whitespace-nowrap">
                          {meetingDate ?? '—'}
                        </TableCell>

                        <TableCell>
                          {assignees.length > 0 ? (
                            <div className="flex items-center gap-2">
                              <div className="flex -space-x-2">
                                {assignees.slice(0, 3).map((a) => (
                                  <Avatar key={a.name} className="size-7 border-2 border-background">
                                    <AvatarImage src={a.image || undefined} alt={a.name} className="object-cover" />
                                    <AvatarFallback className="bg-rose-100 text-rose-700 text-[9px]">
                                      {getInitials(a.name)}
                                    </AvatarFallback>
                                  </Avatar>
                                ))}
                                {assignees.length > 3 && (
                                  <Avatar className="size-7 border-2 border-background">
                                    <AvatarFallback className="bg-muted text-muted-foreground text-[9px]">
                                      +{assignees.length - 3}
                                    </AvatarFallback>
                                  </Avatar>
                                )}
                              </div>
                              <span className="text-xs text-muted-foreground truncate max-w-[80px]">
                                {assignees[0].name}
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">—</span>
                          )}
                        </TableCell>

                        <TableCell>
                          {meeting.tag?.tag_name ? (
                            <Badge
                              variant="outline"
                              className={cn(
                                meeting.tag.tag_name === 'General' && 'bg-blue-500/10 text-blue-600 border-blue-500/20',
                                meeting.tag.tag_name === 'Work' && 'bg-purple-500/10 text-purple-600 border-purple-500/20',
                                meeting.tag.tag_name === 'Social' && 'bg-indigo-500/10 text-indigo-600 border-indigo-500/20',
                              )}
                            >
                              {meeting.tag.tag_name}
                            </Badge>
                          ) : (
                            <span className="text-sm text-muted-foreground">—</span>
                          )}
                        </TableCell>

                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end">
                            <MeetingCardKebab meetingId={meeting.id} />
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
            </div>
          </div>
        ) : isCompletedBoard ? (
          <Kanban
            value={filteredCompletedColumns}
            onValueChange={onCompletedChange}
            getItemValue={(item) => item.id}
            onDragStart={handleCompletedDragStart}
            onDragEnd={handleCompletedDragEnd}
            onDragCancel={handleCompletedDragCancel}
          >
            <KanbanBoard className="flex h-full min-h-0 gap-4 overflow-x-auto xl:grid xl:auto-rows-fr xl:grid-cols-4 xl:overflow-x-hidden xl:min-w-0">
              {COMPLETED_COLUMNS.map((columnValue) => {
                const columnMeetings = filteredCompletedColumns[columnValue] ?? []
                return (
                  <KanbanColumn key={columnValue} value={columnValue} className="w-[280px] shrink-0 h-[520px] xl:w-auto xl:shrink xl:h-auto min-h-0 flex flex-col">
                    <div className="flex items-center justify-between gap-2 shrink-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className={cn(
                            'font-semibold text-sm truncate',
                            columnValue === 'attended' && 'text-blue-600 dark:text-blue-400',
                            columnValue === 'missed' && 'text-amber-600 dark:text-amber-400',
                            columnValue === 'notAttending' && 'text-destructive',
                          )}
                        >
                          {COMPLETED_TITLES[columnValue]}
                        </span>
                        <Badge
                          variant="secondary"
                          className="pointer-events-none rounded-sm shrink-0"
                        >
                          {columnMeetings.length}
                        </Badge>
                      </div>
                      <KanbanColumnHandle asChild>
                        <Button variant="ghost" size="icon" className="size-8 shrink-0">
                          <GripVertical className="size-4" />
                        </Button>
                      </KanbanColumnHandle>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col gap-2 p-0.5">
                      {columnMeetings.map((meeting) => (
                        <KanbanItem key={meeting.id} value={meeting.id} asHandle disabled={false}>
                          <MeetingCardContextMenu
                            meeting={meeting}
                            onEdit={(m) => meetingStore.getState().setMeetingToEdit(m)}
                          >
                            <MeetingCard meeting={meeting} userId={userId ?? undefined} unreadCount={meetingUnreadCounts.get(meeting.id)} embedNativeDrag={false} />
                          </MeetingCardContextMenu>
                        </KanbanItem>
                      ))}
                    </div>
                  </KanbanColumn>
                )
              })}
              <CompletedInsightsCard
                meetings={filteredMeetings}
                currentUserId={userId}
                className="w-[280px] shrink-0 h-[520px] xl:w-auto xl:shrink xl:h-auto"
              />
            </KanbanBoard>
            <KanbanOverlay>
              <div className="size-full rounded-md bg-primary/10 min-w-[260px] min-h-[80px]" />
            </KanbanOverlay>
          </Kanban>
        ) : (
          <Kanban
            value={filteredPendingColumns}
            onValueChange={onPendingChange}
            getItemValue={(item) => item.id}
            onDragStart={handlePendingDragStart}
            onDragEnd={handlePendingDragEnd}
            onDragCancel={handlePendingDragCancel}
          >
            <KanbanBoard className="flex h-full min-h-0 gap-4 overflow-x-auto xl:grid xl:auto-rows-fr xl:grid-cols-4 xl:overflow-x-hidden xl:min-w-0">
              {PENDING_COLUMNS.map((columnValue) => {
                const columnMeetings = filteredPendingColumns[columnValue] ?? []
                return (
                  <KanbanColumn key={columnValue} value={columnValue} className="w-[280px] shrink-0 h-[520px] xl:w-auto xl:shrink xl:h-auto min-h-0 flex flex-col">
                    <div className="flex items-center justify-between gap-2 shrink-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className={cn(
                            'font-semibold text-sm truncate',
                            columnValue === 'noAction' && 'text-slate-600 dark:text-slate-400',
                            columnValue === 'attending' && 'text-green-700 dark:text-green-400',
                            columnValue === 'notAttending' && 'text-destructive',
                            columnValue === 'attended' && 'text-blue-600 dark:text-blue-400',
                          )}
                        >
                          {PENDING_TITLES[columnValue]}
                        </span>
                        <Badge
                          variant="secondary"
                          className="pointer-events-none rounded-sm shrink-0"
                        >
                          {columnMeetings.length}
                        </Badge>
                      </div>
                      <KanbanColumnHandle asChild>
                        <Button variant="ghost" size="icon" className="shrink-0">
                          <GripVertical className="size-4" />
                        </Button>
                      </KanbanColumnHandle>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col gap-2 p-0.5">
                      {columnMeetings.map((meeting) => (
                        <KanbanItem
                          key={meeting.id}
                          value={meeting.id}
                          asHandle
                          disabled={!canDragMeetingOnPendingBoard(meeting, userId)}
                        >
                          <MeetingCardContextMenu
                            meeting={meeting}
                            onEdit={(m) => meetingStore.getState().setMeetingToEdit(m)}
                          >
                            <MeetingCard meeting={meeting} userId={userId ?? undefined} unreadCount={meetingUnreadCounts.get(meeting.id)} embedNativeDrag={false} statusBadgeOverride={columnValue === 'attended' ? 'Awaiting Review' : columnValue === 'notAttending' ? 'Not Attending' : undefined} />
                          </MeetingCardContextMenu>
                        </KanbanItem>
                      ))}
                    </div>
                  </KanbanColumn>
                )
              })}
            </KanbanBoard>
            <KanbanOverlay>
              <div className="size-full rounded-md bg-primary/10 min-w-[260px] min-h-[80px]" />
            </KanbanOverlay>
          </Kanban>
        )}
      </div>
    </>
  )
}
